import {
  MutationCache,
  onlineManager,
  QueryClient,
} from "@tanstack/react-query";

import { ApiError } from "@/lib/api/client";
import { notifyFailed, notifyQueued, notifySaved } from "@/lib/notify";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    /**
     * Every write reports its outcome, from one place.
     *
     * A mutation can opt out with `meta: { silent: true }` -- for writes the
     * screen already narrates, like the survey form's own save line -- or
     * name its own wording with `meta: { success }`. Everything else gets the
     * generic confirmation, which is a great deal better than the silence
     * this replaces.
     */
    mutationCache: new MutationCache({
      /**
       * Say so when a write cannot go out yet.
       *
       * TanStack pauses a mutation while the browser is offline rather than
       * failing it, which is the right behaviour on 4G in Kerala -- but it
       * was invisible: the submit button sat disabled forever, nothing
       * appeared, and the write landed silently minutes later on reconnect.
       * A user seeing nothing clicks again and creates a duplicate.
       */
      onMutate: (_variables, mutation) => {
        if (mutation.options.meta?.silent) return;
        if (!onlineManager.isOnline()) notifyQueued();
      },
      onSuccess: (_data, _variables, _context, mutation) => {
        const meta = mutation.options.meta;
        if (meta?.silent) return;
        notifySaved(
          typeof meta?.success === "string" ? meta.success : undefined,
        );
      },
      onError: (error, _variables, _context, mutation) => {
        if (mutation.options.meta?.silent) return;
        notifyFailed(error);
      },
    }),
    defaultOptions: {
      queries: {
        // Lists go stale quickly; the register changes during a working day.
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // 4xx will not become 2xx on retry, and retrying a 403 just burns
          // the user's time.
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Query keys are hierarchical so invalidation can be precise, and every key
 * carries the active mahallu -- cross-tenant cache bleed is a data-leak class
 * bug, not a cosmetic one.
 */
export const queryKeys = {
  areas: (mahalluId: string) => ["areas", mahalluId] as const,
  houses: {
    list: (mahalluId: string, filters: object) =>
      ["houses", mahalluId, "list", filters] as const,
    detail: (mahalluId: string, id: string) =>
      ["houses", mahalluId, "detail", id] as const,
    // Separate from `list`: a picker's pages accumulate and its cache should
    // not be shared with, or invalidated alongside, a table's single page.
    picker: (mahalluId: string, q: string) =>
      ["houses", mahalluId, "picker", q] as const,
  },
  families: {
    list: (mahalluId: string, filters: object) =>
      ["families", mahalluId, "list", filters] as const,
    picker: (mahalluId: string, q: string) =>
      ["families", mahalluId, "picker", q] as const,
    detail: (mahalluId: string, id: string) =>
      ["families", mahalluId, "detail", id] as const,
  },
  people: {
    list: (mahalluId: string, filters: object) =>
      ["people", mahalluId, "list", filters] as const,
    picker: (mahalluId: string, q: string) =>
      ["people", mahalluId, "picker", q] as const,
    detail: (mahalluId: string, id: string) =>
      ["people", mahalluId, "detail", id] as const,
  },
  summary: (mahalluId: string) => ["summary", mahalluId] as const,
} as const;
